import { useEffect, useState } from "react";
import { ConfirmModal, showToast } from "../primitives";
import type { LibraryNodeDetail } from "../../../lib/types";
import { usePromoteToAgent } from "../../../lib/hooks/use-kuvio";

interface PromoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: LibraryNodeDetail;
}

/**
 * Sprint 5u — promote a folder to an agent. If the folder was previously an
 * agent (it carries a `_demoted_reserve`), offer to restore that persona;
 * otherwise collect fresh instructions describing the role.
 */
export function PromoteModal({ open, onOpenChange, node }: PromoteModalProps) {
  const promote = usePromoteToAgent();
  const reserve = node._demoted_reserve ?? null;
  const hasReserve = !!reserve;
  const [restore, setRestore] = useState(hasReserve);
  const [instructions, setInstructions] = useState("");

  useEffect(() => {
    if (open) {
      setRestore(hasReserve);
      setInstructions("");
    }
  }, [open, hasReserve]);

  const archivedOn = reserve?.archived_at ? reserve.archived_at.slice(0, 10) : null;

  const body = (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <span>
        Promoting <strong>{node.name}</strong> gives it AI capability again — chat,
        instructions, tasks, and schedules. Its children stay in place.
      </span>

      {hasReserve && (
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={restore}
            onChange={(e) => setRestore(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            Restore previous persona
            {archivedOn ? <span style={{ color: "var(--color-text-secondary)" }}> (archived {archivedOn})</span> : null}
            {reserve && reserve.child_memory_count > 0 ? (
              <span style={{ color: "var(--color-text-secondary)" }}>
                {" "}
                · {reserve.child_memory_count} {reserve.child_memory_count === 1 ? "memory" : "memories"} reattach
              </span>
            ) : null}
          </span>
        </label>
      )}

      {!restore && (
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontWeight: 500, color: "var(--color-text-primary)" }}>
            Describe the role this agent plays
          </span>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="e.g. Engineering lead — owns architecture decisions, reviews specs, and tracks the sprint backlog."
            rows={4}
            style={{
              width: "100%",
              resize: "vertical",
              fontFamily: "var(--font-sans)",
              fontSize: 13,
              lineHeight: 1.5,
              padding: 8,
              borderRadius: "var(--border-radius-md)",
              border: "0.5px solid var(--color-border-secondary)",
              background: "var(--color-background-primary)",
              color: "var(--color-text-primary)",
              boxSizing: "border-box",
            }}
          />
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            Optional, but recommended — agents without a real persona are better left as folders.
          </span>
        </label>
      )}
    </div>
  );

  return (
    <ConfirmModal
      open={open}
      onOpenChange={onOpenChange}
      title={`Promote ${node.name} to an agent?`}
      message={body}
      confirmLabel="Promote to agent"
      onConfirm={async () => {
        if (restore && hasReserve) {
          await promote.mutateAsync({ nodeId: node.id, restore_from_reserve: true });
          showToast(`${node.name} restored as an agent`);
        } else {
          const text = instructions.trim();
          await promote.mutateAsync({ nodeId: node.id, instructions: text.length > 0 ? text : undefined });
          showToast(`${node.name} is now an agent`);
        }
      }}
    />
  );
}
